import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateProviderInformation } from '@/app/apis/Providers/requests'
import { useToast } from '@/shad/ui/toast/use-toast'
import { parseRequestError } from '@/common/utilities'
import { UpdateProviderStorePayload } from '@/app/pages/providers/components/configureStore/configure-store.types'
import { ProviderQueryKeys } from '@/queries/providers/queries'

type UpdateProviderInfoPayload = Omit<UpdateProviderStorePayload, 'program_id'>

interface UseUpdateProviderInfoOptions {
  providerId: string
  programId: number
  successTitle?: string
  successDescription?: string
  errorTitle?: string
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export const useUpdateProviderInfo = ({
  providerId,
  programId,
  successTitle = 'Account Owner Information Updated',
  successDescription = 'Account owner information has been updated successfully.',
  errorTitle = 'Account Owner Information Update Failed',
  onSuccess,
  onError,
}: UseUpdateProviderInfoOptions) => {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: UpdateProviderInfoPayload) => {
      const enrichedPayload = {
        ...payload,
        program_id: programId,
      }
      return await updateProviderInformation(providerId, enrichedPayload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ProviderQueryKeys.details(programId, providerId),
      })
      toast({
        title: successTitle,
        description: successDescription,
        variant: 'default',
      })
      onSuccess?.()
    },
    onError: (err: Error) => {
      const msg = parseRequestError(err).message
      toast({
        title: errorTitle,
        description: msg,
        variant: 'destructive',
      })
      onError?.(err)
    },
  })
}
